import { ConversationProvider } from '@elevenlabs/react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CandidateInterviewPage } from './pages/CandidateInterview';
import { InterviewReportPage } from './pages/InterviewReport';
import { JobDetailPage } from './pages/JobDetail';
import { JobsPage } from './pages/Jobs';
import { LoginPage } from './pages/Login';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SESSION_ENDED_EVENT, loadSession } from './lib/auth';

/** Sends signed-out recruiters to the login page, remembering where they were headed. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  // A request that finds the session over - the refresh token expired or was
  // revoked - sends the recruiter to sign in, and back here afterwards, instead
  // of leaving an error on a page that can no longer load anything.
  useEffect(() => {
    const onEnded = () =>
      navigate('/login', { replace: true, state: { from: location.pathname + location.search } });
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, [navigate, location]);

  if (!loadSession()) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <AppShell>{children}</AppShell>;
}

export function App() {
  return (
    <Routes>
      {/*
        Candidates land here from an invite link. No account, no app shell.

        `useConversation` throws unless a ConversationProvider is above it, and
        the throw happens during render, so the whole app unmounts and the
        candidate gets a blank page with the error only in the console. Scoped
        to this route because it is the only one that talks: the provider sets
        up audio plumbing no recruiter screen needs.
      */}
      <Route
        path="/interview"
        element={
          <ErrorBoundary variant="interview">
            <ConversationProvider>
              <CandidateInterviewPage />
            </ConversationProvider>
          </ErrorBoundary>
        }
      />

      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <JobsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/jobs/:jobId"
        element={
          <RequireAuth>
            <JobDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/interviews/:interviewId"
        element={
          <RequireAuth>
            <InterviewReportPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
